import React from 'react';
import { ChevronDown, AlignLeft, AlignCenter, AlignRight, LayoutGrid } from 'lucide-react';
import ct from 'countries-and-timezones';

const IANA_TIMEZONES = Object.values(ct.getAllTimezones())
    .filter(tz => !tz.aliasOf && !tz.name.startsWith('Etc/'))
    .sort((a, b) => a.utcOffset - b.utcOffset)
    .map(tz => ({
        value: tz.name,
        label: `(UTC${tz.utcOffsetStr}) ${tz.name.replace(/_/g, ' ')}`
    }));

/** Etc/* zones are excluded above; UTC is the canonical default for new tenants. */
const TIMEZONE_OPTIONS = [{ value: 'Etc/UTC', label: '(UTC+00:00) UTC' }, ...IANA_TIMEZONES];

export default function BrandingAdvancedTab({ form, update }) {
  const selectTimezone =
    form.timezone == null || form.timezone === '' || form.timezone === 'UTC'
      ? 'Etc/UTC'
      : form.timezone;
  const selectDateFormat = form.date_format || '';
  const selectTimeFormat = form.time_format || '';

  const isWatermarkEnabled = form.watermark_enabled ?? false;
  const watermarkType = form.watermark_type || 'text';
  const watermarkText = form.watermark_text || 'Acme Corp';
  const watermarkPosition = form.watermark_position || 'bottom-right';
  const watermarkOpacity = form.watermark_opacity || 30;
  
  const placements = [
    { id: 'top-left', label: 'Top Left', positionClasses: 'top-1 left-1' },
    { id: 'top-center', label: 'Top Center', positionClasses: 'top-1 left-1/2 -translate-x-1/2' },
    { id: 'top-right', label: 'Top Right', positionClasses: 'top-1 right-1' },
    { id: 'center-left', label: 'Center Left', positionClasses: 'top-1/2 -translate-y-1/2 left-1' },
    { id: 'center', label: 'Center', positionClasses: 'top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2' },
    { id: 'center-right', label: 'Center Right', positionClasses: 'top-1/2 -translate-y-1/2 right-1' },
    { id: 'bottom-left', label: 'Bottom Left', positionClasses: 'bottom-1 left-1' },
    { id: 'bottom-center', label: 'Bottom Center', positionClasses: 'bottom-1 left-1/2 -translate-x-1/2' },
    { id: 'bottom-right', label: 'Bottom Right', positionClasses: 'bottom-1 right-1' },
    { id: 'diagonal-lt-rb', label: 'Diagonal', sub: '(Top Left to Bottom Right)', isDiagonal: true },
    { id: 'diagonal-rt-lb', label: 'Diagonal', sub: '(Top Right to Bottom Left)', isDiagonal: true, invert: true },
    { id: 'tile', label: 'Tile (Repeat)', isTile: true }
  ];

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h3 className="font-bold text-gray-900 dark:text-white text-lg">Advanced Settings</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Configure watermarks, timezones, and platform preferences.</p>
        </div>
      </div>

      {/* Watermark Main Control Section */}
      <div className="border border-gray-100 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-6 shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white">Watermark</h4>
            <p className="text-sm text-gray-500">Add watermark to your application screens and outputs.</p>
          </div>
          <div className={`w-12 h-6 rounded-full flex items-center transition-colors cursor-pointer ${isWatermarkEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`} onClick={() => update('watermark_enabled', !isWatermarkEnabled)}>
            <div className={`w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform ${isWatermarkEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
          </div>
        </div>

        {isWatermarkEnabled && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 animate-in fade-in slide-in-from-top-2 duration-300">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Watermark Type</label>
              <div className="flex bg-gray-50 dark:bg-gray-900 rounded-lg p-1 border border-gray-100 dark:border-gray-700">
                <button 
                  className={`flex-1 py-1.5 text-xs font-bold rounded-md flex items-center justify-center gap-2 ${watermarkType === 'text' ? 'bg-white dark:bg-gray-800 shadow-sm text-blue-600' : 'text-gray-400'}`}
                  onClick={() => update('watermark_type', 'text')}
                >
                  <div className={`w-3 h-3 rounded-full border-2 flex items-center justify-center ${watermarkType === 'text' ? 'border-blue-500' : 'border-gray-300'}`}>
                    {watermarkType === 'text' && <div className="w-1 h-1 bg-blue-500 rounded-full" />}
                  </div>
                  Text
                </button>
                <button 
                  className={`flex-1 py-1.5 text-xs font-bold rounded-md flex items-center justify-center gap-2 ${watermarkType === 'image' ? 'bg-white dark:bg-gray-800 shadow-sm text-blue-600' : 'text-gray-400'}`}
                  onClick={() => update('watermark_type', 'image')}
                >
                  <div className={`w-3 h-3 rounded-full border-2 flex items-center justify-center ${watermarkType === 'image' ? 'border-blue-500' : 'border-gray-300'}`}>
                    {watermarkType === 'image' && <div className="w-1 h-1 bg-blue-500 rounded-full" />}
                  </div>
                  Image
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Text</label>
              <input 
                type="text" 
                value={watermarkText} 
                onChange={(e) => update('watermark_text', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Position</label>
              <div className="relative">
                <select 
                  value={watermarkPosition}
                  onChange={(e) => update('watermark_position', e.target.value)}
                  className="w-full appearance-none bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 text-sm rounded-lg px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {placements.map(p => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
                <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-2.5 pointer-events-none" />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                 <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Opacity</label>
                 <span className="text-xs font-bold text-blue-600">{watermarkOpacity}%</span>
              </div>
              <input 
                 type="range" 
                 min="0" 
                 max="100" 
                 value={watermarkOpacity} 
                 onChange={(e) => update('watermark_opacity', parseInt(e.target.value))}
                 className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
               />
            </div>
          </div>
        )}
      </div>

      {isWatermarkEnabled && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-in fade-in slide-in-from-top-4 duration-500">
          {/* Placement Selector */}
          <div className="border border-gray-100 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-6 shadow-sm">
             <h4 className="font-semibold text-gray-900 dark:text-white mb-1 text-sm">Watermark Placement</h4>
             <p className="text-xs text-gray-500 mb-6">Choose where the watermark should appear.</p>

             <div className="grid grid-cols-3 gap-x-4 gap-y-6">
                {placements.map((p) => (
                   <div 
                     key={p.id}
                     onClick={() => update('watermark_position', p.id)}
                     className="cursor-pointer group"
                   >
                      <div className="flex items-center gap-2 mb-2">
                         <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${watermarkPosition === p.id ? 'border-blue-500' : 'border-gray-300'}`}>
                           {watermarkPosition === p.id && <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />}
                         </div>
                         <span className={`text-[11px] font-medium ${watermarkPosition === p.id ? 'text-gray-900' : 'text-gray-500'}`}>{p.label}</span>
                      </div>

                      <div className={`w-full aspect-[16/9] rounded-lg border bg-gray-50 relative overflow-hidden transition-all ${watermarkPosition === p.id ? 'border-blue-500 bg-blue-50/10' : 'border-gray-100'}`}>
                         <div className="w-full h-2 bg-white border-b border-gray-100 flex items-center px-1 gap-0.5">
                            <div className="w-0.5 h-0.5 rounded-full bg-red-400" />
                            <div className="w-0.5 h-0.5 rounded-full bg-yellow-400" />
                            <div className="w-0.5 h-0.5 rounded-full bg-green-400" />
                         </div>
                         {!p.isDiagonal && !p.isTile && (
                            <span className={`absolute text-blue-500 font-bold text-[10px] opacity-60 ${p.positionClasses}`}>A</span>
                         )}
                         {p.isDiagonal && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-full h-px bg-gray-200 absolute" style={{ transform: `rotate(${p.invert ? '-25deg' : '25deg'})` }} />
                                <span className="text-blue-500 font-bold text-[8px] z-10" style={{ transform: `rotate(${p.invert ? '-25deg' : '25deg'})` }}>A</span>
                            </div>
                         )}
                         {p.isTile && (
                            <div className="absolute inset-0 grid grid-cols-4 grid-rows-2 gap-1 p-1 opacity-30">
                               {Array(8).fill(0).map((_, i) => <span key={i} className="text-blue-500 font-bold text-[6px] flex items-center justify-center">A</span>)}
                            </div>
                         )}
                      </div>
                   </div>
                ))}
             </div>
          </div>

          {/* Real-time Preview */}
          <div className="border border-gray-100 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-6 shadow-sm flex flex-col">
             <h4 className="font-semibold text-gray-900 dark:text-white mb-1 text-sm">Watermark Preview</h4>
             <p className="text-xs text-gray-500 mb-6">This is how the watermark will appear.</p>
             
             <div className="flex-1 bg-gray-50 border border-dashed border-gray-200 rounded-xl p-6 flex items-center justify-center relative overflow-hidden">
                <div className="w-full aspect-[16/10] bg-white border border-gray-200 rounded-lg shadow-md flex overflow-hidden relative max-h-[280px]">
                   <div className="w-16 bg-[#0f172a] h-full flex flex-col pt-3 px-1.5 gap-1.5">
                      <div className="flex items-center gap-1 px-1 mb-2">
                         <div className="w-3 h-3 bg-blue-500 font-bold flex items-center justify-center text-[7px] rounded-sm text-white">A</div>
                         <span className="text-[7px] font-semibold text-white">Acme</span>
                      </div>
                      <div className="w-full h-4 bg-blue-600 rounded flex items-center px-1.5 gap-1">
                         <LayoutGrid className="w-2.5 h-2.5 text-white" />
                         <div className="w-6 h-1 bg-white/50 rounded-sm" />
                      </div>
                      <div className="w-full h-4 bg-gray-800 rounded flex items-center px-1.5 gap-1">
                         <div className="w-2.5 h-2.5 border border-gray-600 rounded-sm" />
                         <div className="w-8 h-1 bg-gray-600 rounded-sm" />
                      </div>
                      <div className="w-full h-4 bg-gray-800 rounded flex items-center px-1.5 gap-1">
                         <div className="w-2.5 h-2.5 border border-gray-600 rounded-sm" />
                         <div className="w-5 h-1 bg-gray-600 rounded-sm" />
                      </div>
                   </div>
                   
                   <div className="flex-1 p-4 flex flex-col gap-4 relative">
                      <div className="flex gap-4">
                         <div className="flex-1">
                            <div className="text-[7px] text-gray-400">Total Executions</div>
                            <div className="text-xs font-bold">24,529 <span className="text-green-500 text-[6px]">+12.5%</span></div>
                         </div>
                         <div className="flex-1">
                            <div className="text-[7px] text-gray-400">Success Rate</div>
                            <div className="text-xs font-bold">98.6% <span className="text-green-500 text-[6px]">+2.3%</span></div>
                         </div>
                      </div>
                      <div className="mt-2">
                         <div className="text-[7px] text-gray-400 mb-2">Executions Over Time</div>
                         <div className="h-20 w-full border-b border-l border-gray-100 flex items-end relative">
                            <svg className="absolute inset-0 h-full w-full" preserveAspectRatio="none" viewBox="0 0 100 100">
                               <polyline points="0,80 20,60 40,70 60,30 80,40 100,10" fill="none" stroke="#3B82F6" strokeWidth="2" />
                            </svg>
                         </div>
                      </div>

                      {/* Watermark in Preview */}
                      {(() => {
                        const style = { opacity: watermarkOpacity / 100 };
                        const content = watermarkType === 'text' ? watermarkText : <span className="text-blue-500 text-2xl">A</span>;
                        
                        if (watermarkPosition === 'tile') {
                          return (
                            <div className="absolute inset-0 flex flex-wrap gap-4 p-2 justify-center items-center pointer-events-none" style={style}>
                               {Array(12).fill(0).map((_, i) => <div key={i} className="text-gray-300 font-bold text-[8px] -rotate-12">{content}</div>)}
                            </div>
                          );
                        }
                        
                        if (watermarkPosition.startsWith('diagonal')) {
                          return (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={style}>
                              <div className="text-gray-300 font-bold text-2xl opacity-40" style={{ transform: `rotate(${watermarkPosition.endsWith('lb') ? '-30deg' : '30deg'})` }}>{content}</div>
                            </div>
                          );
                        }

                        const placement = placements.find(p => p.id === watermarkPosition);
                        const posClass = placement?.positionClasses?.replace(/-1/g, '-4') || 'bottom-4 right-4';
                        return <div className={`absolute text-gray-300 font-bold text-sm pointer-events-none ${posClass}`} style={style}>{content}</div>;
                      })()}
                   </div>
                </div>
             </div>
          </div>
        </div>
      )}

      {/* Timezone Section - Always visible at bottom, or up if watermark hidden */}
      <div className="border border-gray-100 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-6 shadow-sm mt-6">
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Regional Display Settings</h4>
        <p className="text-sm text-gray-500 mb-6">Set the default timezone, date format, and time format for this tenant's environment.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="max-w-md">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Global Tenant Timezone</label>
            <div className="relative">
              <select 
                value={selectTimezone}
                onChange={(e) => update('timezone', e.target.value)}
                className="w-full appearance-none bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 text-sm rounded-lg px-3 py-2.5 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {TIMEZONE_OPTIONS.map(tz => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none" />
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Users can still override this in their own profile settings.
            </p>
          </div>

          <div className="max-w-md">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Date Format</label>
            <div className="relative">
              <select 
                value={selectDateFormat}
                onChange={(e) => update('date_format', e.target.value)}
                className="w-full appearance-none bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 text-sm rounded-lg px-3 py-2.5 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">System Default (YYYY-MM-DD)</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY (e.g. 31/12/2026)</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY (e.g. 12/31/2026)</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD (e.g. 2026-12-31)</option>
                <option value="DD MMM YYYY">DD MMM YYYY (e.g. 31 Dec 2026)</option>
                <option value="MMM DD, YYYY">MMM DD, YYYY (e.g. Dec 31, 2026)</option>
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none" />
            </div>
          </div>

          <div className="max-w-md">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Time Format</label>
            <div className="relative">
              <select 
                value={selectTimeFormat}
                onChange={(e) => update('time_format', e.target.value)}
                className="w-full appearance-none bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 text-sm rounded-lg px-3 py-2.5 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">System Default</option>
                <option value="HH:mm:ss">24-hour (e.g. 14:30:00)</option>
                <option value="hh:mm:ss A">12-hour (e.g. 02:30:00 PM)</option>
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none" />
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-6">Determines the timing of scheduled tasks and execution logs.</p>
      </div>
    </div>
  );
}
